import { createTheme, Theme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { svSE as coreSvSE } from '@mui/material/locale';
import { svSE as dataGridSvSE } from '@mui/x-data-grid/locales';
import { Libraries, useJsApiLoader } from '@react-google-maps/api';
import { AxiosError } from 'axios';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { Auth, getAuth } from 'firebase/auth';
import { Database, getDatabase } from 'firebase/database';
import { Firestore, getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ENV_VARIABLES } from '../config';
import { HttpStatusCode, setAuthFailureHandler } from '../http/http';
import { getOrganisations } from '../services/Organisations';
import { getSelf, logout } from '../services/Users';
import { ArticleTemplateReadView, OrganisationReadView, ScanMode, ThemeInfo, UserReadView } from '../types/types';
import logoWhite from '../utils/img/brand.png';

type GlobalCtxType = {
  firebaseApp: FirebaseApp;
  //firebaseAnalytics: Analytics;
  firebaseDatabase: Database;
  fireStore: Firestore;
  auth: Auth;
  menuMode: ArticleTemplateReadView | undefined;
  setMenuMode: React.Dispatch<React.SetStateAction<ArticleTemplateReadView | undefined>>;
  scanMode: ScanMode | undefined;
  setScanMode: React.Dispatch<React.SetStateAction<ScanMode | undefined>>;
  scannedItems: string[];
  setScannedItems: React.Dispatch<React.SetStateAction<string[]>>;
  scannedItemsRef: React.MutableRefObject<string[]>;
  transformSourceItems: number[];
  setTransformSourceItems: React.Dispatch<React.SetStateAction<number[]>>;
  transformSourceItemsRef: React.MutableRefObject<number[]>;
  scanModeRef: React.RefObject<ScanMode | null>;
  menuModeRef: React.RefObject<ArticleTemplateReadView | null>;
  handleResponseError: (error: AxiosError) => void;
  handleLogOut: () => void;
  user: UserReadView | null;
  setUser: React.Dispatch<React.SetStateAction<UserReadView | null>>;
  /** Organisations the logged-in user is mapped to. Only populated for non-superusers (null for a
   * superuser, since they aren't restricted to any set of organisations); null before it's loaded. */
  myOrganisations: OrganisationReadView[] | null;
  isOrgAdminOf: (orgId: string | number) => boolean;
  currentTheme: ThemeInfo;
  setTheme: (theme: { primaryColor: string | null; logo: string | null }) => void;
  muiTheme: Theme;
  resetTheme: () => void;
  isInIframe: boolean;
  isMapLoaded: boolean;
  mapLoadError: Error | undefined;
  userPosition: GeolocationCoordinates | undefined;
  setUserPosition: React.Dispatch<React.SetStateAction<GeolocationCoordinates | undefined>>;
};

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: ENV_VARIABLES.apiKey,
  authDomain: ENV_VARIABLES.authDomain,
  databaseURL: ENV_VARIABLES.databaseURL,
  projectId: ENV_VARIABLES.projectId,
  storageBucket: ENV_VARIABLES.storageBucket,
  messagingSenderId: ENV_VARIABLES.messagingSenderId,
  appId: ENV_VARIABLES.appId,
  measurementId: ENV_VARIABLES.measurementId
};

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);
//const firebaseAnalytics = getAnalytics(firebaseApp);
const firebaseDatabase = getDatabase(firebaseApp);
const fireStore = getFirestore(firebaseApp);
const auth = getAuth(firebaseApp);
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const functions = getFunctions(firebaseApp);

const initialTheme = createTheme(
  {
    palette: {
      mode: 'light',
      primary: {
        main: '#000927'
      },
      secondary: {
        main: '#4468E1'
      },
      action: {
        disabledBackground: '#4468E185',
        disabled: grey[400]
      }
    }
  },
  dataGridSvSE,
  coreSvSE
);

const defaultTheme = {
  primaryColor: '#000927',
  logo: logoWhite
};

const initialContext: GlobalCtxType = {
  firebaseApp,
  //firebaseAnalytics,
  firebaseDatabase,
  fireStore,
  auth,
  menuMode: undefined,
  setMenuMode: () => {},
  scanMode: undefined,
  setScanMode: () => {},
  scannedItems: [],
  setScannedItems: () => {},
  scannedItemsRef: { current: [] },
  transformSourceItems: [],
  setTransformSourceItems: () => {},
  transformSourceItemsRef: { current: [] },
  scanModeRef: { current: null },
  menuModeRef: { current: null },
  handleLogOut: async () => {},
  handleResponseError: async () => {},
  user: null,
  setUser: () => {},
  myOrganisations: null,
  isOrgAdminOf: () => false,
  currentTheme: defaultTheme,
  setTheme: () => {},
  muiTheme: initialTheme,
  resetTheme: () => {},
  isInIframe: false,
  isMapLoaded: false,
  mapLoadError: undefined,
  userPosition: undefined,
  setUserPosition: () => {}
};

const globalStateCtx = React.createContext<GlobalCtxType>(initialContext);
const MAP_LIBS = ['drawing', 'places'] as Libraries;
const GlobalState = (props: { children: any }) => {
  const { t } = useTranslation();
  const [menuMode, setMenuMode] = useState<ArticleTemplateReadView>();
  const [scanMode, setScanMode] = useState<ScanMode>();
  const [scannedItems, setScannedItems] = useState<string[]>([]);
  const scannedItemsRef = useRef<string[]>([]);
  const [transformSourceItems, setTransformSourceItems] = useState<number[]>([]);
  const transformSourceItemsRef = useRef<number[]>([]);
  const scanModeRef = useRef<ScanMode | null>(null);
  const menuModeRef = useRef<ArticleTemplateReadView | null>(null);
  const [user, setUser] = useState<UserReadView | null>(null);
  const [myOrganisations, setMyOrganisations] = useState<OrganisationReadView[] | null>(null);
  const [currentTheme, setCurrentTheme] = useState<ThemeInfo>(defaultTheme);
  const [muiTheme, setMUITheme] = useState<Theme>(initialTheme);
  const [isInIframe, setIsInIFrame] = useState<boolean>(false);
  const [userPosition, setUserPosition] = useState<GeolocationCoordinates | undefined>();
  const { isLoaded: isMapLoaded, loadError: mapLoadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: ENV_VARIABLES.googleMapsApiKey!,
    libraries: MAP_LIBS
  });

  const resetTheme = () => {
    setTheme(defaultTheme);
  };

  const setTheme = (newTheme: { primaryColor: string | null; logo: string | null }) => {
    const theme = { primaryColor: currentTheme.primaryColor, logo: currentTheme.logo };
    var changed = false;
    if (newTheme.primaryColor && theme.primaryColor !== newTheme.primaryColor) {
      theme.primaryColor = newTheme.primaryColor;
      changed = true;
    }
    if (newTheme.logo && theme.logo !== newTheme.logo) {
      theme.logo = newTheme.logo;
      changed = true;
    }
    if (changed) setCurrentTheme(theme);
  };

  useEffect(() => {
    const token = localStorage.getItem('tractech_token');
    const refreshToken = localStorage.getItem('tractech_refresh_token');
    if (!token && !refreshToken) {
      return;
    }
    setIsInIFrame(window.self !== window.top);
    (async () => {
      const self = await getSelf();
      setUser(self);
    })();
  }, []);

  // Fetches myOrganisations whenever `user` changes, regardless of where it was set from — this
  // mount effect above (restoring a session from stored tokens) is only one of those places;
  // LoginScreen.tsx sets it directly right after a fresh login, without going through here.
  useEffect(() => {
    if (!user) {
      setMyOrganisations(null);
      return;
    }
    if (user.isSuperUser) {
      setMyOrganisations(null);
      return;
    }
    // GET /v2/organisations already returns only the caller's mapped organisations for a
    // non-superuser, so this doubles as "which organisations does this org admin manage".
    (async () => {
      const { records } = await getOrganisations();
      setMyOrganisations(records);
    })();
  }, [user]);

  const isOrgAdminOf = useCallback(
    (orgId: string | number) => myOrganisations?.some((o) => String(o.id) === String(orgId)) ?? false,
    [myOrganisations]
  );

  useEffect(() => {
    setMUITheme(
      createTheme({
        ...initialTheme,
        palette: { ...initialTheme.palette, primary: { main: currentTheme.primaryColor } }
      })
    );
  }, [currentTheme]);

  useEffect(() => {
    scannedItemsRef.current = scannedItems;
  }, [scannedItems]);
  useEffect(() => {
    transformSourceItemsRef.current = transformSourceItems;
  }, [transformSourceItems]);
  useEffect(() => {
    scanModeRef.current = scanMode ?? null;
  }, [scanMode]);
  useEffect(() => {
    menuModeRef.current = menuMode ?? null;
  }, [menuMode]);

  // Watch user position
  useEffect(() => {
    // Throttle position updates to prevent excessive re-renders
    let lastUpdateTime = 0;
    const THROTTLE_MS = 3000; // Update at most every 3 seconds

    const id = navigator.geolocation.watchPosition(
      (position) => {
        const now = Date.now();
        // Only update if enough time has passed since last update
        if (now - lastUpdateTime >= THROTTLE_MS) {
          setUserPosition(position.coords);
          lastUpdateTime = now;
        }
      },
      (error) => {
        // Only log error, don't cause re-renders
        console.warn('Geolocation watch error:', error);
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 60000 // Cache position for 60 seconds to reduce updates
      }
    );
    return () => {
      navigator.geolocation.clearWatch(id);
    };
  }, []);

  // Centralized logout function - single source of truth for logout behavior
  const handleLogOut = useCallback(() => {
    const session = localStorage.getItem('tractech_session_id');
    if (session != null) {
      // Fire-and-forget logout call - no need to await before clearing
      logout(session).catch(() => {});
    }
    setUser(null);
    localStorage.clear();
    const origin = window.location.origin;
    window.location.replace(`${origin}/login`);
  }, [setUser]);

  const handleResponseError = useCallback(
    (e: AxiosError) => {
      // Check for timeout errors (ECONNABORTED is Axios's timeout error code)
      if (e.code === 'ECONNABORTED' || e.message.includes('timeout')) {
        toast.error(t('error.requestTimeout'));
        return;
      }

      if (e.response) {
        if (e.response.status === HttpStatusCode.Unauthorized) {
          // Auth error - use centralized logout
          handleLogOut();
        } else if (
          e.response.status === HttpStatusCode.Forbidden &&
          e.response.data &&
          typeof e.response.data === 'object' &&
          'code' in e.response.data &&
          e.response.data.code === 'E0003'
        ) {
          // Caller isn't mapped to the organisation this action targets - not an auth failure, don't log out.
          toast.error(t('error.forbiddenForOrganisation'));
        } else {
          // Other errors - show toast message
          const errorMessage =
            e.response.data && typeof e.response.data === 'object' && 'message' in e.response.data
              ? e.response.data.message
              : e.message;
          toast.error(errorMessage as string);
        }
      } else if (e.request) {
        // Request was made but no response received (network error, timeout, etc.)
        toast.error(t('error.networkError'));
      }
    },
    [handleLogOut, t]
  );

  // Register auth failure handler with HTTP layer
  useEffect(() => {
    setAuthFailureHandler(handleResponseError);
  }, [handleResponseError]);

  return (
    <globalStateCtx.Provider
      value={{
        firebaseApp,
        //firebaseAnalytics,
        firebaseDatabase,
        fireStore,
        auth,
        menuMode,
        setMenuMode,
        scanMode,
        setScanMode,
        scannedItems,
        setScannedItems,
        scannedItemsRef,
        transformSourceItems,
        setTransformSourceItems,
        transformSourceItemsRef,
        scanModeRef,
        menuModeRef,
        handleLogOut,
        handleResponseError,
        user,
        setUser,
        myOrganisations,
        isOrgAdminOf,
        currentTheme,
        setTheme,
        muiTheme,
        resetTheme,
        isInIframe,
        isMapLoaded,
        mapLoadError,
        userPosition,
        setUserPosition
      }}
    >
      {props.children}
    </globalStateCtx.Provider>
  );
};

export default GlobalState;
export const useGlobalState = () => React.useContext(globalStateCtx);
